/** The record editor's `duration` widget: a range of hours + minutes (`DurationRange`) on
 *  a FieldSlot, over ONE key whose value is `{min, max}` in hours.
 *
 *  ONE error caption: the slot's. The widget shows none of its own (`inlineErrors={false}`)
 *  — a half-typed bound reaches the slot through `onInvalid`, the range rule through
 *  `checkValue`, the server's errors as for any field — so an error takes the slot's
 *  reserved caption line and moves nothing. View mode is the range in words on the
 *  inputs' height (`formatChange`, the catalog's `formatHoursRange`). Clearing both bounds
 *  is null — no duration — the value the server stores and reads back. */
import { useMemo } from 'react';

import type { RecordWidget, RecordWidgetProps } from '../../editor/types';
import { formatHoursRange } from '../../time/hours';

import { DurationRange } from './DurationRange';
import { asDurationValue, durationRangeError } from './durationRangeModel';

function DurationRangeInput(p: RecordWidgetProps) {
  const value = useMemo(() => asDurationValue(p.value), [p.value]);
  return (
    <DurationRange
      id={p.id}
      value={value}
      // Both bounds empty is no duration — null, as the server stores and reads it.
      onChange={(v) => p.onChange(v.min === null && v.max === null ? null : v)}
      onInvalid={p.onInvalid}
      onFocus={p.onFocus}
      onBlur={p.onBlur}
      disabled={p.disabled}
      labelledBy={p.labelId}
      describedBy={p.describedBy}
      inlineErrors={false}
    />
  );
}

export const DurationRangeWidget: RecordWidget = Object.assign(DurationRangeInput, {
  formatChange: (v: unknown) => {
    const d = asDurationValue(v);
    return formatHoursRange(d.min, d.max);
  },
  checkValue: (to: unknown) => durationRangeError(asDurationValue(to)),
});
